import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader } from "storybook/internal/components";
import { BASE_URL, FILES_BASE_URL } from "../../../api/baseURL";
import {
  removeSessionDocument,
  validateClaimDocument,
} from "../../../api/vistaService";
import CustomCheckbox from "../../../components/common/customCheckbox/customCheckbox";
import DragDropFileUpload from "../../../components/common/dragDropFileUpload";
import FileUploadClaim from "../../../components/common/fileUploadInput/fileUploadInputForClaims";
import TelInput from "../../../components/common/telInput/telInput";
import TitleDescription from "../../../components/newMotorPolicy/titleDescription/titleDescription";
import useFileClaimFlow from "../../../hooks/useFileClaimFlow";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import useMotorForm from "../../../hooks/useMotorFormData";
import useValidation from "../../../hooks/useValidation";
import { getClientSource } from "../../../utils/getClientSource";
import {
  FILE_CLAIM_FLOW,
  FILE_CLAIM_NON_MOTOR_POLICY_HOLDER,
  UPLOAD_DOCS_INPUT,
} from "./data";
import FileClaimMotorLayout from "./fileClaimMotorLayout";
import {
  claimSessionFileId,
  findingsAnswerOvertaken,
  mergeCrossChecks,
  noteFindingsApplied,
  takeFindingsTicket,
} from "./useVistaSession";
import ValidationNotices, { findingLines } from "./validationNotices";
import {
  BODILY_INJURY,
  PROPERTY_DAMAGE,
  VEHICLE_DAMAGE,
} from "./NonSolidarityUser/data";

export const DriverLicencesDocs = "DriverLicence";
export const AccidentDocs = "AccidentDocs";
export const OwnershipDocs = "OwnershipDocs";
export const IncidentInput = "IncidentInput";
export const InsuranceCert = "InsuranceCert";
export const PropertyOwner = "PropertyOwner";
export const ProofAddress = "ProofAddress";
export const ImpactedPersonCprDocs = "ImpactedPersonCprDocs";
export const JudicialReportDocs = "JudicialReportDocs";
export const OtherDocs = "OtherDocs";
export const PropertyAccidentPhotosDocs = "propertyAccidentPhotosDocs";

export const IBAN_DOC = "ibanDoc";
export const MOBLIE_DATA = "MOBLIE_DATA";
export const OWNER_SHIP_DOCS = "ownerShipCardDocs";
export const PHOTOS_OF_DAMAGE = "photosOfDamage";
export const POLICE_REPORTS = "policeReports";

// Vista Motor slot labels for validate-as-you-go, keyed by upload slot name.
// Labels must match Vista's Motor document slots exactly (same labels
// buildDocumentTypesArray produces). Slots without a mapping are not validated.
const VISTA_SLOT_LABELS = {
  driver_license: (index) =>
    index === 0 ? "Driving License 1" : "Driving License 2",
  vehicle_ownership_certificate: (index) =>
    index === 0 ? "Vehicle Ownership 1" : "Vehicle Ownership 2",
  photos_of_accident: () => "Accident Photos",
  vd_clear_photos_of_the_accident: () => "Accident Photos",
  incident_report: () => "GDT Accident Report",
};

// Vista omits involvedDocuments on missing-data warnings (raw-probe
// 2026-07-14), so pin every known check to the slot(s) whose documents it
// inspects. Same map as uploadDocStepPage.
const CHECK_NAME_SLOTS = {
  "Chassis Number Extracted": ["vehicle_ownership_certificate"],
  "License Number Extracted": ["driver_license"],
  "Date of Birth Extracted": ["driver_license"],
  "Accident Date Validity": ["incident_report"],
  "Owner Name Consistency": [
    "vehicle_ownership_certificate",
    "incident_report",
  ],
  "Address Consistency": ["vehicle_ownership_certificate", "driver_license"],
  "Plate Number Consistency": [
    "vehicle_ownership_certificate",
    "incident_report",
  ],
  accident_photos_vehicle_no_with_gdt_report_validation: [
    "photos_of_accident",
    "vd_clear_photos_of_the_accident",
    "incident_report",
  ],
};

// Map a Vista label back to the upload slot(s) it belongs to, so cross-document
// findings can be shown on every implicated document (never blame one side).
const slotNamesForVistaLabel = (label) => {
  if (!label) return [];
  if (label.startsWith("Driving License")) return ["driver_license"];
  if (label.startsWith("Vehicle Ownership"))
    return ["vehicle_ownership_certificate"];
  if (label === "Accident Photos")
    return ["photos_of_accident", "vd_clear_photos_of_the_accident"];
  if (label === "GDT Accident Report") return ["incident_report"];
  return [];
};

// Which upload slot(s) a cross-document finding rests on. Vista omits
// involvedDocuments on missing-data warnings, so fall back to the pinned map
// and then to the slot whose upload produced the finding.
const involvedSlotsOf = (check) => {
  const involved = check.involvedDocuments || [];
  if (involved.length > 0) {
    return involved.flatMap((doc) => slotNamesForVistaLabel(doc.documentType));
  }
  return (
    CHECK_NAME_SLOTS[check.checkName] ||
    (check._sourceSlot ? [check._sourceSlot] : [])
  );
};

function UploadDocsPage(
  { selectedType, sessionRef, policyNumber, driverCpr },
  { selectedInsuredType }
) {
  const [formData, setFormData] = useState({});
  const [values, setValues] = useState({});
  const { errors, validateFields } = useValidation();
  const [isLoading, setIsLoading] = useState(false); // useState instead of var
  const translate = useLanguageTranslate();
  const { t } = useTranslation();
  const { handleNextSubstep, flowType } = useFileClaimFlow();

  const validateFileLinks = () => {
    const allDocs = [
      ...drivingLicenseDocs,
      ...accidentPhotosDocs,
      ...vehicleOwnershipCertificateDocs,
      ...insuranceCertificateDocs,
      ...proofOfPropertyOwnershipDocs,
      ...propertyAccidentPhotosDocs,
      ...impactedPersonCprDocs,
      ...judicialReportDocs,
      ...otherDocs,
      ...ownerShipCardDocs,
      ...photosOfDamage,
      ...policeReports,
      ...incidentDocs,
    ];

    // Check if ibanCertificate file exists and has an invalid link
    if (
      ibanCertificate.file &&
      typeof ibanCertificate.file === "string" &&
      !ibanCertificate.file.startsWith("https://files-api.solidarity.bh")
    ) {
      toast.error(
        translate(
          "Some files were not uploaded correctly. Please remove and re-upload them.",
          "لم يتم تحميل بعض الملفات بشكل صحيح. يرجى إزالتها وإعادة تحميلها."
        )
      );
      return false;
    }

    const invalidLinks = allDocs.filter(
      (doc) =>
        doc.preview &&
        !doc.preview.startsWith("https://files-api.solidarity.bh")
    );

    if (invalidLinks.length > 0) {
      toast.error(
        translate(
          "Some files were not uploaded correctly. Please remove and re-upload them.",
          "لم يتم تحميل بعض الملفات بشكل صحيح. يرجى إزالتها وإعادة تحميلها."
        )
      );
      return false;
    }
    return true;
  };

  const continueClickHandler = async () => {
    if (!validateForm()) return; // early return if validation fails
    if (!validateFileLinks()) return; // early return if file links are invalid

    setIsLoading(true);
    await saveDriverLicenceDocs(drivingLicenseDocs);
    await saveAccidentDocs(accidentPhotosDocs);
    await saveOwnershipDocs(vehicleOwnershipCertificateDocs);
    await saveInsuranceCertDocs(insuranceCertificateDocs);
    await savePropertyOwnerCprDocs(proofOfPropertyOwnershipDocs);
    await saveProofOfPropertyOwnershipDocs(proofOfPropertyOwnershipDocs);
    await savePropertyAccidentPhotosDocs(propertyAccidentPhotosDocs);
    await saveImpactedPersonCprDocs(impactedPersonCprDocs);
    await saveJudicialReportDocs(judicialReportDocs);
    await saveOtherDocs(otherDocs);

    // non motor docs
    await saveOwnerShipCardDocs(ownerShipCardDocs);
    await savePhotosOfDamage(photosOfDamage);
    await savePoliceReports(policeReports);
    await saveIbanDocument(ibanCertificate);
    await saveIncidentCertificate(incidentDocs);
    await saveMobileData(values);

    if (flowType != FILE_CLAIM_NON_MOTOR_POLICY_HOLDER) {
      const insuranceCertificates = insuranceCertificateDocs.map(
        (doc) => doc.preview
      );
      const ownerShipsNonMotors = ownerShipCardDocs.map((doc) => doc.preview);
      const damagesDocs = photosOfDamage.map((doc) => doc.preview);
      const policeReportsNonMotor = policeReports.map((doc) => doc.preview);
      const incidentReports = incidentDocs.map((doc) => doc.preview);

      const combined = [
        ...insuranceCertificates,
        ...ownerShipsNonMotors,
        ...damagesDocs,
        ...policeReportsNonMotor,
        ...incidentReports,
      ];

      if (ibanCertificate.file) {
        combined.push(ibanCertificate.file);
      }

      if (
        selectedInsuredType != VEHICLE_DAMAGE &&
        flowType != "FILE_CLAIM_VEHICLE_DAMAGE_FLOW"
      ) {
        await SubmitToNonMotorClaim(combined);
      }
    } else {
      const insuranceCertificates = insuranceCertificateDocs.map(
        (doc) => doc.preview
      );
      const ownerShipsNonMotors = ownerShipCardDocs.map((doc) => doc.preview);
      const damagesDocs = photosOfDamage.map((doc) => doc.preview);
      const policeReportsNonMotor = policeReports.map((doc) => doc.preview);

      const combined = [
        ...insuranceCertificates,
        ...ownerShipsNonMotors,
        ...damagesDocs,
        ...policeReportsNonMotor,
      ];

      if (ibanCertificate.file) {
        combined.push(ibanCertificate.file);
      }

      if (
        selectedInsuredType != VEHICLE_DAMAGE &&
        flowType != "FILE_CLAIM_VEHICLE_DAMAGE_FLOW"
      ) {
        await SubmitToNonMotorClaim(combined);
      }
    }

    setIsLoading(false);
    handleNextSubstep();
  };

  const SubmitToNonMotorClaim = async (links) => {
    const url = BASE_URL + "/MailToCompany/emailToCompany";

    const data = {
      // Your data to send in the request body
      Channel: getClientSource(),
      ToTitle: "Non Motor Claim",
      ToMessage:
        "We have received a Non Motor Claim from the online portal. Kindly find the details below.",
      ToDepartment: "",
      FromEmail: "ayman.mayoof@solidarity.com.bh",
      ToEmail: "nonmotor.claims@solidarity.com.bh",
      subject: "Non-Motor Claim",
      message: "",
      formType: "Plain",
      MobileNumber: values.mobile ?? "",
      // Email: values.email ?? "",
      Subject: selectedType ?? "",
      FormType: "Plain",
      attachmentLink: links,
      ToEmail: "abdulrahman.janahi@solidarity.com.bh",
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", // Set headers for JSON
        },
        body: JSON.stringify(data), // Convert data to JSON string
      });

      if (!response.ok) {
        setIsLoading(false);
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      console.log("Success:", result);
      setIsLoading(false);

      return result;
    } catch (error) {
      setIsLoading(false);

      console.error("Error:", error);
    }
  };

  // SOLIDARITY USER using same docs inVehicle Damage

  const [ibanCertificate, setIbanCertificate] = useState({
    fileName: "",
    fileSize: "",
    file: null,
    base64file: "",
    ownershipData: "",
    error: "",
  });

  const { saveMotorDataInRedux: saveIbanDocument } = useMotorForm(
    IBAN_DOC,
    setIbanCertificate
  );

  const { saveMotorDataInRedux: saveMobileData } = useMotorForm(
    MOBLIE_DATA,
    setValues
  );

  // Non-Solidarity Vehicle Damage
  const [drivingLicenseDocs, setDrivingLicenseDocs] = useState([]);
  const { saveMotorDataInRedux: saveDriverLicenceDocs } = useMotorForm(
    DriverLicencesDocs,
    setDrivingLicenseDocs
  );

  const [insuranceCertificateDocs, setInsuranceCertificateDocs] = useState([]);
  const { saveMotorDataInRedux: saveInsuranceCertDocs } = useMotorForm(
    InsuranceCert,
    setInsuranceCertificateDocs
  );

  const [vehicleOwnershipCertificateDocs, setVehicleOwnershipCertificateDocs] =
    useState([]);
  const { saveMotorDataInRedux: saveOwnershipDocs } = useMotorForm(
    OwnershipDocs,
    setVehicleOwnershipCertificateDocs
  );

  const [accidentPhotosDocs, setAccidentPhotosDocs] = useState([]);
  const { saveMotorDataInRedux: saveAccidentDocs } = useMotorForm(
    AccidentDocs,
    setAccidentPhotosDocs
  );

  const [incidentDocs, setIncidentInput] = useState([]);
  const { saveMotorDataInRedux: saveIncidentCertificate } = useMotorForm(
    IncidentInput,
    setIncidentInput
  );

  // Non-Solidarity Property Damage
  const [propertyOwnerCprDocs, setPropertyOwnerCprDocs] = useState([]);
  const { saveMotorDataInRedux: savePropertyOwnerCprDocs } = useMotorForm(
    PropertyOwner,
    setPropertyOwnerCprDocs
  );

  const [proofOfPropertyOwnershipDocs, setProofOfPropertyOwnershipDocs] =
    useState([]);
  const { saveMotorDataInRedux: saveProofOfPropertyOwnershipDocs } =
    useMotorForm(ProofAddress, setProofOfPropertyOwnershipDocs);

  const [propertyAccidentPhotosDocs, setPropertyAccidentPhotosDocs] = useState(
    []
  );
  const { saveMotorDataInRedux: savePropertyAccidentPhotosDocs } = useMotorForm(
    PropertyAccidentPhotosDocs,
    setPropertyAccidentPhotosDocs
  );

  // Non-Solidarity Bodily Injury
  const [impactedPersonCprDocs, setImpactedPersonCprDocs] = useState([]);
  const { saveMotorDataInRedux: saveImpactedPersonCprDocs } = useMotorForm(
    ImpactedPersonCprDocs,
    setImpactedPersonCprDocs
  );

  const [judicialReportDocs, setJudicialReportDocs] = useState([]);
  const { saveMotorDataInRedux: saveJudicialReportDocs } = useMotorForm(
    JudicialReportDocs,
    setJudicialReportDocs
  );

  const [otherDocs, setOtherDocs] = useState([]);
  const { saveMotorDataInRedux: saveOtherDocs } = useMotorForm(
    OtherDocs,
    setOtherDocs
  );

  // Non Motor Solidarity Flow

  const [ownerShipCardDocs, setOwnerShipCardDocs] = useState([]);
  const { saveMotorDataInRedux: saveOwnerShipCardDocs } = useMotorForm(
    OWNER_SHIP_DOCS,
    setOwnerShipCardDocs
  );

  const [photosOfDamage, setPhotosOfDamage] = useState([]);
  const { saveMotorDataInRedux: savePhotosOfDamage } = useMotorForm(
    PHOTOS_OF_DAMAGE,
    setPhotosOfDamage
  );

  const [policeReports, setPoliceReports] = useState([]);
  const { saveMotorDataInRedux: savePoliceReports } = useMotorForm(
    POLICE_REPORTS,
    setPoliceReports
  );

  const handleUploadComplete = (file) => {
    //RequestTokenAPI(file.name +"_"+ generateUniqueIdentifier());
    console.log(`Upload complete for ${file}`);
  };
  const [isChecked, setIsChecked] = useState(false);
  const uploadingFilesRef = useRef(new Set());
  const [isUploading, setIsUploading] = useState(false);

  // Vista validate-as-you-go state. Per-document verdicts are advisory here;
  // a cross-document check flagged `blocking` does hold Continue (round 2).
  const [docValidations, setDocValidations] = useState({}); // slotName -> { status }
  const [crossChecks, setCrossChecks] = useState({}); // checkKey -> crossValidation check
  const vistaRateLimitUntilRef = useRef(0);
  // The Vista session handles this page's files hold, and the files deleted
  // while Vista was still reading them — whose answer describes a document
  // that has left the claim and is dropped rather than shown.
  const claimedFileIdsRef = useRef(new Set());
  const removedKeysRef = useRef(new Set());
  // Vista reads still in flight. Counted per file rather than read off the
  // slot badge, which a slot of ten accident photos overwrites with the first
  // verdict back while the other nine are still being read.
  const [validatingCount, setValidatingCount] = useState(0);

  const setSlotValidation = (slotName, status, extra) => {
    setDocValidations((prev) => ({ ...prev, [slotName]: { status, ...extra } }));
  };

  // Which Vista slot index each file holds, keyed by slot then by file key.
  // On a two-sided document the index picks the label, so a replacement has
  // to claim the index its predecessor freed. A running count could only go
  // up: delete "Driving License 1", upload again, and the replacement went
  // out as a second "Driving License 2" with Vista never seeing side 1.
  const vistaSlotIndexRef = useRef({});

  const claimVistaSlotIndex = (slotName, key) => {
    const taken = (vistaSlotIndexRef.current[slotName] ||= {});
    if (Number.isInteger(taken[key])) return taken[key];
    const used = new Set(Object.values(taken));
    let index = 0;
    while (used.has(index)) index += 1;
    taken[key] = index;
    return index;
  };

  const releaseVistaSlotIndex = (slotName, key) => {
    const taken = vistaSlotIndexRef.current[slotName];
    if (taken) delete taken[key];
  };

  // Fire-and-forget per-document Vista validation. Motor flows only (opt-in
  // via sessionRef); per-document results are informational, though a
  // cross-document check Vista marks `blocking` does hold Continue.
  const runVistaValidation = (file, slotName) => {
    if (!sessionRef) return;
    if (flowType == FILE_CLAIM_NON_MOTOR_POLICY_HOLDER) return;
    const labelForIndex = VISTA_SLOT_LABELS[slotName];
    if (!labelForIndex) return;
    if (Date.now() < vistaRateLimitUntilRef.current) return;

    const documentType = labelForIndex(
      Math.min(claimVistaSlotIndex(slotName, file._key), 1)
    );

    setSlotValidation(slotName, "validating");
    setValidatingCount((count) => count + 1);

    // Claimed before the request goes out, so a deletion asked for after this
    // read cannot be applied over the findings the read comes back with.
    const findingsTicket = takeFindingsTicket();
    validateClaimDocument({
      file,
      documentType,
      sessionRef,
      policyNumber,
      driverCpr,
    }).then((result) => {
      if (removedKeysRef.current.has(file._key)) {
        // Deleted mid-read. Vista finished anyway — the document is in the
        // session now — so it goes straight back out, named by the handle this
        // very response carries. Without a handle there is no naming it
        // without taking the slot's other files too, so it is left alone.
        claimSessionFileId(
          file,
          documentType,
          result?.sessionFiles,
          claimedFileIdsRef.current
        );
        if (file._vistaFileId) {
          claimedFileIdsRef.current.delete(file._vistaFileId);
          dropFromVistaSession(documentType, file._vistaFileId, slotName);
        }
        return;
      }
      if (!result) {
        setSlotValidation(slotName, "idle");
        return;
      }
      if (result.rateLimited) {
        vistaRateLimitUntilRef.current =
          Date.now() + (result.retryAfterSeconds || 60) * 1000;
        setSlotValidation(slotName, "idle");
        return;
      }
      if (result.scanFailed) {
        setSlotValidation(slotName, "scan_failed");
        return;
      }
      // Cross-document findings describe the whole session's document set,
      // so merge them even when this particular file failed its own check.
      const checks = result.crossValidation?.checks || [];
      if (checks.length) {
        noteFindingsApplied(findingsTicket);
        setCrossChecks((prev) => mergeCrossChecks(prev, checks, slotName));
      }
      // The handle Vista minted for this file, which is the only way to name
      // it again when the customer deletes it.
      claimSessionFileId(
        file,
        documentType,
        result.sessionFiles,
        claimedFileIdsRef.current
      );
      if (result.wrongDocument) {
        // documentValidation says the type check failed. Advisory on this
        // legacy page — never blocks Continue.
        setSlotValidation(slotName, "wrong_document");
        return;
      }
      if (!result.docValid) {
        // Vista could not read this document (wrong type / bad capture).
        // Advisory on this legacy page — never blocks Continue.
        setSlotValidation(slotName, "unreadable");
        return;
      }
      // Raw extracted fields, kept on the file (through redux) so Review can
      // enrich the CRM createclaim payload with them. Only a verified
      // document's extraction is trustworthy enough to forward.
      file._vistaExtraction = result.data;
      // Issuing country / non-Bahraini flag from documentValidation[0]
      // (2026-08-17) — Review skips the accident location for a foreign
      // report so it is never resolved to a Bahraini governorate.
      const docValidation = Array.isArray(result.data?.documentValidation)
        ? result.data.documentValidation[0]
        : null;
      file._vistaCountry = docValidation?.country || null;
      file._vistaForeign = docValidation?.foreign === true;
      setSlotValidation(slotName, "done", {
        foreign: docValidation?.foreign === true,
      });
    })
      .finally(() => setValidatingCount((count) => Math.max(0, count - 1)));
  };

  // A deleted document leaves the page and Vista's session together.
  //
  // The findings that rested on this slot go immediately, before Vista is even
  // asked: they were computed against a document set that no longer exists, so
  // holding Continue on one would be complaining about a file the customer can
  // no longer see. Slot-wide, because the photo slot labels every file
  // "Accident Photos" and there is no telling which of them a finding used.
  //
  // Vista's answer then replaces them with the checks re-run over what is
  // left, so a blocking finding that the deletion resolved clears here rather
  // than on the next upload.
  const handleVistaFileRemoved = (remainingInSlot, slotName, removedFile) => {
    if (!sessionRef) return;
    if (flowType == FILE_CLAIM_NON_MOTOR_POLICY_HOLDER) return;
    const labelForIndex = VISTA_SLOT_LABELS[slotName];
    if (!labelForIndex) return;

    const remaining = Array.isArray(remainingInSlot) ? remainingInSlot : [];
    const remainingKeys = remaining.map((doc) => doc?._key);
    // Free the Vista slot index the deleted file held, so its replacement
    // takes that index back instead of the next one up.
    const taken = vistaSlotIndexRef.current[slotName] || {};
    const documentType = labelForIndex(
      Math.min(taken[removedFile?._key] ?? 0, 1)
    );
    Object.keys(taken).forEach((key) => {
      if (!remainingKeys.includes(key)) releaseVistaSlotIndex(slotName, key);
    });
    if (removedFile?._key) removedKeysRef.current.add(removedFile._key);
    // The slot's own per-document badge described the file that just went.
    setSlotValidation(slotName, "idle");

    setCrossChecks((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (involvedSlotsOf(next[key]).includes(slotName)) delete next[key];
      });
      return next;
    });

    // And take it out of Vista's session, or the checks go on cross-checking
    // the next upload against the document the customer just deleted. One
    // file of a multi-file slot is named by the handle Vista minted for it;
    // without a handle the label alone would take its siblings too, so a file
    // Vista never read is only removed when it is the slot's only one.
    const fileId = removedFile?._vistaFileId;
    if (fileId) claimedFileIdsRef.current.delete(fileId);
    // No handle and a read still out for it: Vista adds the document when that
    // read lands, so removing it now would race the request putting it there.
    // The read's own response takes it out instead.
    if (!fileId && docValidations[slotName]?.status === "validating") return;
    if (
      !fileId &&
      remaining.some(
        (doc) => labelForIndex(Math.min(taken[doc?._key] ?? 0, 1)) === documentType
      )
    ) {
      console.warn(
        `[Vista] no session handle for the deleted "${documentType}" — leaving the session untouched rather than dropping its siblings`
      );
      return;
    }

    dropFromVistaSession(documentType, fileId, slotName);
  };

  const dropFromVistaSession = (documentType, fileId, slotName) => {
    const findingsTicket = takeFindingsTicket();
    removeSessionDocument({ sessionRef, documentType, fileId }).then(
      (result) => {
        // A failed call or an expired session leaves what the delete already
        // cleared.
        const checks = result?.crossValidation?.checks;
        if (!checks) return;
        // This answer describes the session as it stood when the file went;
        // a read that has landed since has already moved it on.
        if (findingsAnswerOvertaken(findingsTicket)) {
          console.warn(
            `[Vista] ignoring the removal answer for "${documentType}" — a later read has already answered`
          );
          return;
        }
        noteFindingsApplied(findingsTicket);
        setCrossChecks((prev) => {
          // Vista re-ran the checks over what is left, so its answer is the
          // truth about the slot the file came out of: a blocking check about
          // the deleted document clears here, without waiting for the
          // customer's next upload. Only that slot, though — the response
          // carries the checks Vista chose to re-run, not every check that
          // still stands.
          const kept = {};
          Object.entries(prev).forEach(([key, check]) => {
            if (!involvedSlotsOf(check).includes(slotName)) kept[key] = check;
          });
          return mergeCrossChecks(kept, checks, slotName);
        });
      }
    );
  };

  // Shaped for ValidationNotices: one finding per row, worst first, the same
  // vocabulary the per-document wizard uses. This page used to stack its own
  // `mt-1` rectangles, which is how several separate findings ended up reading
  // as one paragraph of red.
  const getSlotNotices = (slotName) => {
    const notices = [];
    if (docValidations[slotName]?.status === "scan_failed") {
      notices.push({
        key: "scan_failed",
        severity: "error",
        text: translate(
          "This document failed our security scan. Please re-capture and upload it again.",
          "لم يجتز هذا المستند الفحص الأمني. يرجى إعادة التقاطه وتحميله مرة أخرى."
        ),
      });
    }
    if (docValidations[slotName]?.status === "wrong_document") {
      notices.push({
        key: "wrong_document",
        severity: "error",
        text: translate(
          "This doesn't look like the right document for this field. Please upload the correct document.",
          "لا يبدو أن هذا هو المستند الصحيح لهذا الحقل. يرجى تحميل المستند الصحيح."
        ),
      });
    }
    if (docValidations[slotName]?.status === "unreadable") {
      notices.push({
        key: "unreadable",
        severity: "error",
        text: translate(
          "We couldn't read this document. Please check it's the right document and clearly photographed.",
          "لم نتمكن من قراءة هذا المستند. يرجى التأكد من أنه المستند الصحيح وأن الصورة واضحة."
        ),
      });
    }
    // Vista recognised a non-Bahraini document in this slot. Display only:
    // neutral, never blocking, and deliberately silent about the relaxed
    // cross-document checks (claims wording, same copy as the wizard).
    if (docValidations[slotName]?.foreign) {
      notices.push({
        key: "foreign",
        severity: "info",
        text: translate(
          "Non-Bahraini document/s identified",
          "تم تحديد مستند/مستندات غير بحرينية"
        ),
      });
    }
    Object.entries(crossChecks).forEach(([checkKey, check]) => {
      if (check.status === "pass") return;
      // "Could not validate: …" warnings mean a prerequisite document
      // hasn't been uploaded or read yet — not user-actionable, skip.
      if (
        check.status === "warning" &&
        (check.description || "").startsWith("Could not validate")
      )
        return;
      if (involvedSlotsOf(check).includes(slotName)) {
        // One row per finding: a check carrying several of them — a plate
        // missing from the photos for each of four vehicles — is a row each
        // rather than one paragraph.
        findingLines(check, translate).forEach((line, part) => {
          notices.push({
            key: `check:${checkKey}:${part}`,
            // Vista flags a non-pass result `blocking: true` when it must
            // stop Continue (round 2 handover 2026-08-17 §1); the rest is
            // advisory.
            severity:
              check.status === "fail" || check.blocking === true
                ? "error"
                : "warning",
            text: line,
          });
        });
      }
    });
    return notices;
  };

  const renderVistaNotices = (slotName) => {
    const notices = getSlotNotices(slotName);
    const isValidating = docValidations[slotName]?.status === "validating";
    if (!notices.length && !isValidating) return null;
    return (
      <div className="mb-2">
        {isValidating && (
          <p className="text-[#5B6871] text-xs mt-1">
            {translate("Checking document…", "جارٍ التحقق من المستند…")}
          </p>
        )}
        <ValidationNotices notices={notices} />
      </div>
    );
  };

  const UploadDoc = async (file, type) => {
    const fileId = `${type}_${file.name}`;
    uploadingFilesRef.current.add(fileId);
    setIsUploading(true);

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = async () => {
        try {
          const base64String = reader.result.split(",")[1];
          const tokenResult = await RequestTokenAPI(
            file.name + "_" + generateUniqueIdentifier()
          );
          const uploaded = await UploadAPI(
            tokenResult.data.token,
            file.name,
            base64String
          );
          const requestedLink = await RequestLinkAPI(uploaded.data.file_id);

          // Advisory Vista validation — runs in the background, never blocks
          runVistaValidation(file, type);

          resolve(requestedLink.data.link);
        } catch (error) {
          reject(error);
        } finally {
          uploadingFilesRef.current.delete(fileId);
          if (uploadingFilesRef.current.size == 0) setIsUploading(false);
        }
      };

      reader.onerror = (error) => {
        uploadingFilesRef.current.delete(fileId);
        setIsUploading(uploadingFilesRef.current.size > 0);
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  };

  function generateUniqueIdentifier() {
    // Get current timestamp for uniqueness
    const timestamp = Date.now().toString(36); // Converts timestamp to base-36 (0-9, a-z)

    // Generate a random number and convert it to base-36 as well
    const randomNumber = Math.floor(Math.random() * 1_000_000_000).toString(36);

    // Concatenate the timestamp and random number
    return `${timestamp}-${randomNumber}`;
  }
  const onFileUploaded = (value, name) => {
    setFormData({ ...formData, [name]: value });

    console.log(`Upload complete for ${formData}`);
  };

  // Sample function to make a POST API call
  const RequestTokenAPI = async (id) => {
    const url = FILES_BASE_URL + "/tokens/request";
    const data = {
      // Your data to send in the request body
      secret: "Claim123",
      agent: getClientSource(),
      item_type: "claim_docs",
      item_id: id,
      source: getClientSource(),
      description: "Files token request from customer portal in claims.",
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", // Set headers for JSON
        },
        body: JSON.stringify(data), // Convert data to JSON string
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      console.log("Success:", result);
      return result;
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const UploadAPI = async (token, name, base64) => {
    const url = FILES_BASE_URL + "/files/upload";
    const data = {
      // Your data to send in the request body
      token: token,
      source: getClientSource(),
      file_name: name,
      file_base64: base64,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", // Set headers for JSON
        },
        body: JSON.stringify(data), // Convert data to JSON string
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      console.log("Success:", result);
      return result;
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const RequestLinkAPI = async (fileId) => {
    const url = FILES_BASE_URL + "/links/request";
    const data = {
      // Your data to send in the request body
      file_id: fileId,
      ip: "",
      validity: 7200,
      reference: "customer portal",
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", // Set headers for JSON
        },
        body: JSON.stringify(data), // Convert data to JSON string
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      console.log("Success:", result);
      return result;
    } catch (error) {
      console.error("Error:", error);
    }
  };
  console.log(flowType, "flowType");

  const { docs } = useMemo(() => {
    if (flowType == FILE_CLAIM_FLOW) {
      console.log("solidarity_user");
      return UPLOAD_DOCS_INPUT.solidarity_user;
    } else if (flowType == FILE_CLAIM_NON_MOTOR_POLICY_HOLDER) {
      console.log("solidarity_user_non_motor");
      console.log("Insurance Type: " + selectedType);
      return UPLOAD_DOCS_INPUT.solidarity_user_non_motor;
    } else if (
      selectedInsuredType == VEHICLE_DAMAGE ||
      flowType == "FILE_CLAIM_VEHICLE_DAMAGE_FLOW"
    ) {
      console.log("non_solidarity_vehicle_damage");
      return UPLOAD_DOCS_INPUT.non_solidarity_vehicle_damage;
    } else if (
      selectedInsuredType == PROPERTY_DAMAGE ||
      flowType == "FILE_CLAIM_PROPERTY_DAMAGE_FLOW"
    ) {
      console.log("non_solidarity_property_damage");
      return UPLOAD_DOCS_INPUT.non_solidarity_property_damage;
    } else if (
      selectedInsuredType == BODILY_INJURY ||
      flowType == "FILE_CLAIM_BODILY_INJURY_FLOW"
    ) {
      console.log("non_solidarity_bodily_injury");
      return UPLOAD_DOCS_INPUT.non_solidarity_bodily_injury;
    } else {
      return [];
    }
  }, [flowType, selectedInsuredType]);
  // console.log(docs, "some docs");

  // console.log(selectedInsuredType, "selectedInsuredType");

  const validateForm = (data) => {
    let config = [];
    docs?.forEach((each) => {
      let data = {
        name: each?.name,
        validations: ["file"],
      };
      if (!each.isOptional) config.push(data);
    });
    if (flowType == FILE_CLAIM_NON_MOTOR_POLICY_HOLDER) {
      config.push({
        name: "mobile",
        validations: ["number", "length"],
        min_length: 8,
      });
    }
    console.log(config, "some config");
    let isValid = validateFields(config, {
      // Solidarity User
      driver_license: drivingLicenseDocs,
      vehicle_ownership_certificate: vehicleOwnershipCertificateDocs,
      photos_of_accident: accidentPhotosDocs,

      // Non-Solidarity Vehicle Damage
      insurance_certificate: insuranceCertificateDocs,
      vd_clear_photos_of_the_accident: accidentPhotosDocs,

      // Non-Solidarity Property Damage
      property_owner_cpr_number: propertyOwnerCprDocs,
      proof_of_property_ownership: proofOfPropertyOwnershipDocs,
      clear_photos_of_the_accident: propertyAccidentPhotosDocs,

      // Non-Solidarity Bodily Injury
      cpr_number_of_the_impacted_person: impactedPersonCprDocs,
      judicial_report: judicialReportDocs,
      other_docs: otherDocs,
      incident_report: incidentDocs,

      // non motor flow
      mobile: data ? data?.mobile : values.mobile,
      ownership_card: ownerShipCardDocs,
      photos_of_damages: photosOfDamage,
      police_report: policeReports,
      iban_letter_of_owner: ibanCertificate,
    });

    console.log("Check " + data);
    console.log("Check ", isValid);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const update = { ...values, [name]: value };
    setValues(update);
    if (errors?.[name]) validateForm(update);
  };

  console.log(flowType, " flow type");
  // console.log(selectedInsuredType, " selectedInsuredType");

  console.log(flowType, "some flow type");

  console.log(errors, "some errors");
  // console.log(validationConfig, 'some errors')
  console.log(accidentPhotosDocs, "accident photos doc");
  console.log("drivingLicenseDocs", drivingLicenseDocs);
  console.log("insuranceCertificates", insuranceCertificateDocs);
  console.log(
    "vehicleOwnershipCertificateDocs",
    vehicleOwnershipCertificateDocs
  );
  console.log("incidentReports", incidentDocs);

  const hasBlockingCrossCheck = Object.values(crossChecks).some(
    (check) => check.status !== "pass" && check.blocking === true
  );

  return (
    <FileClaimMotorLayout
      btn_disabled={
        (flowType == FILE_CLAIM_NON_MOTOR_POLICY_HOLDER && !isChecked) ||
        isUploading ||
        // A document Vista is still reading has no verdict yet, and a
        // blocking cross-check may be one response away.
        validatingCount > 0 ||
        hasBlockingCrossCheck
      }
      // Held while a read is out for the same reason, and because leaving the
      // page throws away the answer it was waiting for.
      back_disabled={isUploading || validatingCount > 0}
      btn_onClick={continueClickHandler}
    >
      <div className="mx-auto bg-white max767:p-5 w-[500px] max-w-[100%] pb-20">
        <div className="max767:hidden">
          <TitleDescription
            title={t("motorInsurance.fileAClaimFlow.repairOrCashPage.title")}
            description={t(
              "motorInsurance.fileAClaimFlow.repairOrCashPage.description"
            )}
          />
        </div>
        <div className="max767:block hidden max767:mb-6">
          <TitleDescription
            title={t(
              "motorInsurance.fileAClaimFlow.repairOrCashPage.mobile_title"
            )}
            description={t(
              "motorInsurance.fileAClaimFlow.repairOrCashPage.mobile_description"
            )}
          />
        </div>

        {flowType == FILE_CLAIM_FLOW && (
          <div>
            <div>
              <div>
                <DragDropFileUpload
                  label={translate(
                    UPLOAD_DOCS_INPUT?.solidarity_user?.docs?.[0]?.label_en,
                    UPLOAD_DOCS_INPUT?.solidarity_user?.docs?.[0]?.label_ar
                  )}
                  onUploadComplete={handleUploadComplete}
                  name={UPLOAD_DOCS_INPUT?.solidarity_user?.docs?.[0]?.name}
                  multiple
                  toggleFileUpload={onFileUploaded}
                  onFileRemoved={handleVistaFileRemoved}
                  uploadDocFunc={UploadDoc}
                  uploadedFiles={drivingLicenseDocs || []}
                  maxFiles={2}
                  setUploadedFiles={setDrivingLicenseDocs}
                />
                {errors?.[
                  UPLOAD_DOCS_INPUT?.solidarity_user?.docs?.[0]?.name
                ] && (
                  <p className="text-red-500 text-sm">
                    {t("validation.required")}
                  </p>
                )}
                {renderVistaNotices("driver_license")}
              </div>
              <div>
                <DragDropFileUpload
                  label={translate(
                    UPLOAD_DOCS_INPUT?.solidarity_user?.docs?.[1]?.label_en,
                    UPLOAD_DOCS_INPUT?.solidarity_user?.docs?.[1]?.label_ar
                  )}
                  onUploadComplete={handleUploadComplete}
                  name={UPLOAD_DOCS_INPUT?.solidarity_user?.docs?.[1]?.name}
                  multiple
                  toggleFileUpload={onFileUploaded}
                  onFileRemoved={handleVistaFileRemoved}
                  uploadDocFunc={UploadDoc}
                  maxFiles={2}
                  uploadedFiles={vehicleOwnershipCertificateDocs || []}
                  setUploadedFiles={setVehicleOwnershipCertificateDocs}
                />
                {errors?.[
                  UPLOAD_DOCS_INPUT?.solidarity_user?.docs?.[1]?.name
                ] && (
                  <p className="text-red-500 text-sm">
                    {t("validation.required")}
                  </p>
                )}
                {renderVistaNotices("vehicle_ownership_certificate")}
              </div>
              <div>
                <DragDropFileUpload
                  label={translate(
                    UPLOAD_DOCS_INPUT?.solidarity_user?.docs?.[2]?.label_en,
                    UPLOAD_DOCS_INPUT?.solidarity_user?.docs?.[2]?.label_ar
                  )}
                  onUploadComplete={handleUploadComplete}
                  name={UPLOAD_DOCS_INPUT?.solidarity_user?.docs?.[2]?.name}
                  multiple
                  toggleFileUpload={onFileUploaded}
                  onFileRemoved={handleVistaFileRemoved}
                  uploadDocFunc={UploadDoc}
                  uploadedFiles={accidentPhotosDocs || []}
                  maxFiles={10}
                  setUploadedFiles={setAccidentPhotosDocs}
                />
                {errors?.[
                  UPLOAD_DOCS_INPUT?.solidarity_user?.docs?.[2]?.name
                ] && (
                  <p className="text-red-500 text-sm">
                    {t("validation.required")}
                  </p>
                )}
                {renderVistaNotices("photos_of_accident")}
              </div>
            </div>
            <div></div>
          </div>
        )}

        {(selectedInsuredType == PROPERTY_DAMAGE ||
          flowType == "FILE_CLAIM_PROPERTY_DAMAGE_FLOW") && (
          <div>
            <div>
              <DragDropFileUpload
                label={translate(
                  UPLOAD_DOCS_INPUT?.non_solidarity_property_damage?.docs?.[0]
                    ?.label_en,
                  UPLOAD_DOCS_INPUT?.non_solidarity_property_damage?.docs?.[0]
                    ?.label_ar
                )}
                onUploadComplete={handleUploadComplete}
                name={
                  UPLOAD_DOCS_INPUT?.non_solidarity_property_damage?.docs?.[0]
                    ?.name
                }
                multiple
                toggleFileUpload={onFileUploaded}
                onFileRemoved={handleVistaFileRemoved}
                uploadDocFunc={UploadDoc}
                uploadedFiles={propertyOwnerCprDocs || []}
                setUploadedFiles={setPropertyOwnerCprDocs}
              />
              {errors?.[
                UPLOAD_DOCS_INPUT?.non_solidarity_property_damage?.docs?.[0]
                  ?.name
              ] && (
                <p className="text-red-500 text-sm">
                  {t("validation.required")}
                </p>
              )}
            </div>
            <div>
              <DragDropFileUpload
                label={translate(
                  UPLOAD_DOCS_INPUT?.non_solidarity_property_damage?.docs?.[1]
                    ?.label_en,
                  UPLOAD_DOCS_INPUT?.non_solidarity_property_damage?.docs?.[1]
                    ?.label_ar
                )}
                onUploadComplete={handleUploadComplete}
                name={
                  UPLOAD_DOCS_INPUT?.non_solidarity_property_damage?.docs?.[1]
                    ?.name
                }
                multiple
                toggleFileUpload={onFileUploaded}
                onFileRemoved={handleVistaFileRemoved}
                uploadDocFunc={UploadDoc}
                uploadedFiles={proofOfPropertyOwnershipDocs || []}
                setUploadedFiles={setProofOfPropertyOwnershipDocs}
              />
              {errors?.[
                UPLOAD_DOCS_INPUT?.non_solidarity_property_damage?.docs?.[1]
                  ?.name
              ] && (
                <p className="text-red-500 text-sm">
                  {t("validation.required")}
                </p>
              )}
            </div>
            <div>
              <DragDropFileUpload
                label={translate(
                  UPLOAD_DOCS_INPUT?.non_solidarity_property_damage?.docs?.[2]
                    ?.label_en,
                  UPLOAD_DOCS_INPUT?.non_solidarity_property_damage?.docs?.[2]
                    ?.label_ar
                )}
                onUploadComplete={handleUploadComplete}
                name={
                  UPLOAD_DOCS_INPUT?.non_solidarity_property_damage?.docs?.[2]
                    ?.name
                }
                multiple
                // Vista's Accident Photos slot does not read a PDF (confirmed
                // live — see the wizard's `imagesOnly`); same slot, same rule.
                acceptedTypes={["image/jpeg", "image/png"]}
                toggleFileUpload={onFileUploaded}
                onFileRemoved={handleVistaFileRemoved}
                uploadDocFunc={UploadDoc}
                uploadedFiles={propertyAccidentPhotosDocs || []}
                setUploadedFiles={setPropertyAccidentPhotosDocs}
              />
              {errors?.[
                UPLOAD_DOCS_INPUT?.non_solidarity_property_damage?.docs?.[2]
                  ?.name
              ] && (
                <p className="text-red-500 text-sm">
                  {t("validation.required")}
                </p>
              )}
            </div>
          </div>
        )}

        {(selectedInsuredType == BODILY_INJURY ||
          flowType == "FILE_CLAIM_BODILY_INJURY_FLOW") && (
          <div>
            <div>
              <DragDropFileUpload
                label={translate(
                  UPLOAD_DOCS_INPUT?.non_solidarity_bodily_injury?.docs?.[0]
                    ?.label_en,
                  UPLOAD_DOCS_INPUT?.non_solidarity_bodily_injury?.docs?.[0]
                    ?.label_ar
                )}
                onUploadComplete={handleUploadComplete}
                name={
                  UPLOAD_DOCS_INPUT?.non_solidarity_bodily_injury?.docs?.[0]
                    ?.name
                }
                multiple
                toggleFileUpload={onFileUploaded}
                onFileRemoved={handleVistaFileRemoved}
                uploadDocFunc={UploadDoc}
                uploadedFiles={impactedPersonCprDocs || []}
                setUploadedFiles={setImpactedPersonCprDocs}
              />
              {errors?.[
                UPLOAD_DOCS_INPUT?.non_solidarity_bodily_injury?.docs?.[0]?.name
              ] && (
                <p className="text-red-500 text-sm">
                  {t("validation.required")}
                </p>
              )}
            </div>
            <div>
              <DragDropFileUpload
                label={translate(
                  UPLOAD_DOCS_INPUT?.non_solidarity_bodily_injury?.docs?.[1]
                    ?.label_en,
                  UPLOAD_DOCS_INPUT?.non_solidarity_bodily_injury?.docs?.[1]
                    ?.label_ar
                )}
                onUploadComplete={handleUploadComplete}
                name={
                  UPLOAD_DOCS_INPUT?.non_solidarity_bodily_injury?.docs?.[1]
                    ?.name
                }
                multiple
                toggleFileUpload={onFileUploaded}
                onFileRemoved={handleVistaFileRemoved}
                uploadDocFunc={UploadDoc}
                uploadedFiles={judicialReportDocs || []}
                setUploadedFiles={setJudicialReportDocs}
              />
              {errors?.[
                UPLOAD_DOCS_INPUT?.non_solidarity_bodily_injury?.docs?.[1]?.name
              ] && (
                <p className="text-red-500 text-sm">
                  {t("validation.required")}
                </p>
              )}
            </div>
            <div>
              <DragDropFileUpload
                label={translate(
                  UPLOAD_DOCS_INPUT?.non_solidarity_bodily_injury?.docs?.[2]
                    ?.label_en,
                  UPLOAD_DOCS_INPUT?.non_solidarity_bodily_injury?.docs?.[2]
                    ?.label_ar
                )}
                onUploadComplete={handleUploadComplete}
                name={
                  UPLOAD_DOCS_INPUT?.non_solidarity_bodily_injury?.docs?.[2]
                    ?.name
                }
                multiple
                toggleFileUpload={onFileUploaded}
                onFileRemoved={handleVistaFileRemoved}
                uploadDocFunc={UploadDoc}
                uploadedFiles={otherDocs || []}
                setUploadedFiles={setOtherDocs}
              />
              {errors?.[
                UPLOAD_DOCS_INPUT?.non_solidarity_bodily_injury?.docs?.[2]?.name
              ] && (
                <p className="text-red-500 text-sm">
                  {t("validation.required")}
                </p>
              )}
            </div>
          </div>
        )}

        {flowType == FILE_CLAIM_NON_MOTOR_POLICY_HOLDER &&
          (!isLoading ? (
            <div>
              <TelInput
                name="mobile"
                value={values.mobile}
                error={errors.mobile}
                onChange={handleChange}
                label={translate("Mobile Number", "رقم الجوال")}
                className="mobilenumber px-4 py-3 max767:w-[50%] w-[97%] border-r-0 border-0 rounded-[9px] h-[46px] focus:outline-none"
              />
              <div className="mt-[-30px]">
                <DragDropFileUpload
                  label={translate(
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[0]
                      ?.label_en,
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[0]
                      ?.label_ar
                  )}
                  onUploadComplete={handleUploadComplete}
                  name={
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[0]
                      ?.name
                  }
                  multiple
                  toggleFileUpload={onFileUploaded}
                  onFileRemoved={handleVistaFileRemoved}
                  uploadDocFunc={UploadDoc}
                  uploadedFiles={insuranceCertificateDocs || []}
                  setUploadedFiles={setInsuranceCertificateDocs}
                />
                {errors?.[
                  UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[0]?.name
                ] && (
                  <p className="text-red-500 text-sm">
                    {t("validation.required")}
                  </p>
                )}
              </div>
              <div>
                <DragDropFileUpload
                  label={translate(
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[1]
                      ?.label_en,
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[1]
                      ?.label_ar
                  )}
                  onUploadComplete={handleUploadComplete}
                  name={
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[1]
                      ?.name
                  }
                  multiple
                  toggleFileUpload={onFileUploaded}
                  onFileRemoved={handleVistaFileRemoved}
                  maxFiles={2}
                  uploadDocFunc={UploadDoc}
                  uploadedFiles={ownerShipCardDocs || []}
                  setUploadedFiles={setOwnerShipCardDocs}
                />
                {errors?.[
                  UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[1]?.name
                ] && (
                  <p className="text-red-500 text-sm">
                    {t("validation.required")}
                  </p>
                )}
              </div>
              <div>
                <DragDropFileUpload
                  label={translate(
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[2]
                      ?.label_en,
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[2]
                      ?.label_ar
                  )}
                  onUploadComplete={handleUploadComplete}
                  name={
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[2]
                      ?.name
                  }
                  multiple
                  toggleFileUpload={onFileUploaded}
                  onFileRemoved={handleVistaFileRemoved}
                  uploadDocFunc={UploadDoc}
                  maxFiles={10}
                  uploadedFiles={photosOfDamage || []}
                  setUploadedFiles={setPhotosOfDamage}
                />
                {errors?.[
                  UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[2]?.name
                ] && (
                  <p className="text-red-500 text-sm">
                    {t("validation.required")}
                  </p>
                )}
              </div>
              <div>
                <DragDropFileUpload
                  label={translate(
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[3]
                      ?.label_en,
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[3]
                      ?.label_ar
                  )}
                  onUploadComplete={handleUploadComplete}
                  name={
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[3]
                      ?.name
                  }
                  multiple
                  toggleFileUpload={onFileUploaded}
                  onFileRemoved={handleVistaFileRemoved}
                  uploadDocFunc={UploadDoc}
                  uploadedFiles={policeReports || []}
                  setUploadedFiles={setPoliceReports}
                />
                {errors?.[
                  UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[3]?.name
                ] && (
                  <p className="text-red-500 text-sm">
                    {t("validation.required")}
                  </p>
                )}
              </div>
              <div className="uploaddocs">
                <label className="mt-6 flex text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px]">
                  {translate(
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[4]
                      ?.label_en,
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[4]
                      ?.label_ar
                  )}
                </label>
                <FileUploadClaim
                  className="platetypedocuinput uploaddisable"
                  name={
                    UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[4]
                      ?.name
                  }
                  toggleFileUpload={onFileUploaded}
                  onFileRemoved={handleVistaFileRemoved}
                  formState={ibanCertificate}
                  setFormState={setIbanCertificate}
                />
                {errors?.[
                  UPLOAD_DOCS_INPUT?.solidarity_user_non_motor?.docs?.[4]?.name
                ] && (
                  <p className="text-red-500 text-sm">
                    {t("validation.required")}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <Loader />
          ))}

        {(selectedInsuredType == VEHICLE_DAMAGE ||
          flowType == "FILE_CLAIM_VEHICLE_DAMAGE_FLOW") && (
          <div>
            <div>
              <DragDropFileUpload
                label={translate(
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[0]
                    ?.label_en,
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[0]
                    ?.label_ar
                )}
                onUploadComplete={handleUploadComplete}
                name={
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[0]
                    ?.name
                }
                multiple
                maxFiles={2}
                toggleFileUpload={onFileUploaded}
                onFileRemoved={handleVistaFileRemoved}
                uploadDocFunc={UploadDoc}
                uploadedFiles={drivingLicenseDocs || []}
                setUploadedFiles={setDrivingLicenseDocs}
              />
              {errors?.[
                UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[0]
                  ?.name
              ] && (
                <p className="text-red-500 text-sm">
                  {t("validation.required")}
                </p>
              )}
              {renderVistaNotices("driver_license")}
            </div>
            <div>
              <DragDropFileUpload
                label={translate(
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[1]
                    ?.label_en,
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[1]
                    ?.label_ar
                )}
                onUploadComplete={handleUploadComplete}
                name={
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[1]
                    ?.name
                }
                multiple
                toggleFileUpload={onFileUploaded}
                onFileRemoved={handleVistaFileRemoved}
                uploadDocFunc={UploadDoc}
                uploadedFiles={insuranceCertificateDocs || []}
                setUploadedFiles={setInsuranceCertificateDocs}
              />
              {errors?.[
                UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[1]
                  ?.name
              ] && (
                <p className="text-red-500 text-sm">
                  {t("validation.required")}
                </p>
              )}
            </div>
            <div>
              <DragDropFileUpload
                label={translate(
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[2]
                    ?.label_en,
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[2]
                    ?.label_ar
                )}
                onUploadComplete={handleUploadComplete}
                name={
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[2]
                    ?.name
                }
                multiple
                toggleFileUpload={onFileUploaded}
                onFileRemoved={handleVistaFileRemoved}
                uploadDocFunc={UploadDoc}
                maxFiles={2}
                uploadedFiles={vehicleOwnershipCertificateDocs || []}
                setUploadedFiles={setVehicleOwnershipCertificateDocs}
              />
              {errors?.[
                UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[2]
                  ?.name
              ] && (
                <p className="text-red-500 text-sm">
                  {t("validation.required")}
                </p>
              )}
              {renderVistaNotices("vehicle_ownership_certificate")}
            </div>
            <div>
              <DragDropFileUpload
                label={translate(
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[3]
                    ?.label_en,
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[3]
                    ?.label_ar
                )}
                onUploadComplete={handleUploadComplete}
                name={
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[3]
                    ?.name
                }
                multiple
                toggleFileUpload={onFileUploaded}
                onFileRemoved={handleVistaFileRemoved}
                uploadDocFunc={UploadDoc}
                uploadedFiles={accidentPhotosDocs || []}
                setUploadedFiles={setAccidentPhotosDocs}
              />
              {errors?.[
                UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[3]
                  ?.name
              ] && (
                <p className="text-red-500 text-sm">
                  {t("validation.required")}
                </p>
              )}
              {renderVistaNotices("vd_clear_photos_of_the_accident")}
            </div>
            {/* <div>
              <DragDropFileUpload
                label={translate(
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[4]
                    ?.label_en,
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[4]
                    ?.label_ar
                )}
                onUploadComplete={handleUploadComplete}
                name={
                  UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[4]
                    ?.name
                }
                multiple
                toggleFileUpload={onFileUploaded}
                onFileRemoved={handleVistaFileRemoved}
                uploadDocFunc={UploadDoc}
                uploadedFiles={incidentDocs || []}
                setUploadedFiles={setIncidentInput}
              />
              {errors?.[
                UPLOAD_DOCS_INPUT?.non_solidarity_vehicle_damage?.docs?.[4]
                  ?.name
              ] && (
                <p className="text-red-500 text-sm">
                  {t("validation.required")}
                </p>
              )}
            </div> */}
          </div>
        )}

        {flowType != FILE_CLAIM_NON_MOTOR_POLICY_HOLDER && (
          <div>
            <div>
              <DragDropFileUpload
                label={t(
                  "motorInsurance.fileAClaimFlow.repairOrCashPage.report_file_label"
                )}
                onUploadComplete={handleUploadComplete}
                name={"incident_report"}
                multiple
                toggleFileUpload={onFileUploaded}
                onFileRemoved={handleVistaFileRemoved}
                uploadDocFunc={UploadDoc}
                uploadedFiles={incidentDocs || []}
                setUploadedFiles={setIncidentInput}
              />
              {errors?.["incident_report"] && (
                <p className="text-red-500 text-sm">
                  {t("validation.required")}
                </p>
              )}
              {renderVistaNotices("incident_report")}
            </div>
          </div>
        )}

        {flowType == FILE_CLAIM_NON_MOTOR_POLICY_HOLDER && (
          <div className="flex items-start gap-2 self-stretch px-0 py-1 mb-4 mt-4">
            <CustomCheckbox
              Label={
                <div className="flex flex-col">
                  <label
                    htmlFor="agreement"
                    className="text-[#252C32] text-sm not-italic font-normal leading-6 max767:break-all"
                  >
                    <p>
                      {translate(
                        "By submitting this form, you agree to be contacted regarding your request and you are confirming you agree to our Terms of Use and Privacy Policy.",
                        "من خلال تقديم هذا النموذج، فإنك توافق على أن يتم التواصل معك بشأن طلبك وتؤكد موافقتك على شروط الاستخدام وسياسة الخصوصية الخاصة بنا."
                      )}
                    </p>
                  </label>
                </div>
              }
              isChecked={isChecked}
              setIsChecked={setIsChecked}
            />
          </div>
        )}
      </div>
    </FileClaimMotorLayout>
  );
}

export default UploadDocsPage;
